'use client';

/**
 * PlanAssignments — the operator surface to DEFINE published plans and ASSIGN them
 * to organizations with PER-CLIENT pricing (isolated overrides, no cascade).
 *
 * - Published Plans: create/list shared tier templates (POST/GET /shared-tiers).
 *   Supports all models: flat_fee, per_seat (PUPM), pmpm (PMPM), tiered,
 *   revenue_share (reimbursement), hybrid.
 * - Assign to org: pick org + plan and set per-client OVERRIDES across the plan's
 *   models (base, per-user/member rate, setup, discount, reimbursement %, credits,
 *   terms) → POST /tier-assignments. Overrides live on the assignment and are
 *   isolated to that org (COALESCE(override, template)) — they never cascade.
 *   A live cost preview computes the effective monthly via /plan-tiers/{id}/calculate.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Building2, Plus, X, ShieldCheck, Check } from 'lucide-react';
import { openRevopsDocument } from './openRevopsDocument';

const PLATFORM_BACKEND_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_API_URL) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WEAVEIN_API_URL) ||
  'https://weavein-api.onrender.com';

function getAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const tenantSlug = typeof window !== 'undefined' ? localStorage.getItem('platform_tenant_slug') : null;
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  if (cboId) h['X-CBO-ID'] = cboId;
  if (tenantSlug) h['X-Tenant-ID'] = tenantSlug;
  return h;
}
async function rev<T = any>(p: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops${p}`, { ...opts, headers: { ...getAuthHeaders(), ...opts.headers } });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.detail || `HTTP ${res.status}`);
  return res.json();
}

const MODELS = [
  { value: 'flat_fee', label: 'Flat fee' },
  { value: 'per_seat', label: 'Per user / month (PUPM)' },
  { value: 'pmpm', label: 'Per member / month (PMPM)' },
  { value: 'tiered', label: 'Tiered / volume' },
  { value: 'revenue_share', label: 'Reimbursement / revenue share' },
  { value: 'hybrid', label: 'Hybrid' },
];
const modelLabel = (m: string) => MODELS.find((x) => x.value === m)?.label || m;
const hasBase = (m: string) => ['flat_fee', 'per_seat', 'pmpm', 'hybrid'].includes(m);
const hasPerUnit = (m: string) => ['per_seat', 'pmpm', 'hybrid'].includes(m);
const perUnitLabel = (m: string) => (m === 'pmpm' ? 'Per member / mo' : 'Per user / mo');
const unitNoun = (m: string) => (m === 'pmpm' ? 'members' : 'seats');
const hasRevShare = (m: string) => ['revenue_share', 'hybrid'].includes(m);

interface Org { id: number; name: string }
interface Tier {
  id: string; tier_code: string; tier_name: string; pricing_model: string;
  base_monthly_price: number; per_user_price: number; setup_fee: number;
  annual_discount_percent: number; revenue_share_config: any;
  included_communication_credits: number; included_ai_credits: number;
}
interface Assignment {
  id: string; cbo_id: number; org_name: string | null; tier_name: string;
  pricing_model: string; status: string; effective_from: string; effective_until?: string | null;
  pricing_mode?: string | null; approval_requested_by?: string | null; approval_reason?: string | null;
  override_per_user_price?: number | null; override_setup_fee?: number | null; override_base_monthly_price?: number | null;
}
interface PricingDiff {
  mode: 'list' | 'custom';
  lines: { field: string; label: string; list: number; custom: number; discount_percent: number }[];
  needs_approval: boolean; reasons: string[]; threshold_percent: number;
}
const num = (s: string) => (s === '' ? undefined : parseFloat(s));

export default function PlanAssignments() {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [pending, setPending] = useState<Assignment[]>([]);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [orgRes, tierRes, aRes, pRes] = await Promise.all([
        fetch(`${PLATFORM_BACKEND_URL}/api/cbos`, { headers: getAuthHeaders() }).then((r) => (r.ok ? r.json() : [])),
        rev<{ shared_tiers: Tier[] }>('/shared-tiers').catch(() => ({ shared_tiers: [] })),
        rev<{ assignments: Assignment[] }>('/tier-assignments').catch(() => ({ assignments: [] })),
        rev<{ assignments: Assignment[] }>('/tier-assignments?status=pending_approval').catch(() => ({ assignments: [] })),
      ]);
      setPending(pRes.assignments || []);
      setOrgs((Array.isArray(orgRes) ? orgRes : orgRes.cbos || []).map((o: any) => ({ id: o.id, name: o.name })));
      setTiers(tierRes.shared_tiers || []);
      setAssignments(aRes.assignments || []);
    } catch (e: any) { setError(e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeAssignments = useMemo(() => assignments.filter((a) => a.status === 'active'), [assignments]);

  const removeAssignment = async (a: Assignment) => {
    if (!confirm(`Remove the ${a.tier_name} plan from ${a.org_name || `cbo ${a.cbo_id}`}? It will no longer apply.`)) return;
    setError(null); setOk(null);
    try {
      await rev(`/tier-assignments/${a.id}`, { method: 'DELETE' });
      setOk('Plan removed from organization.');
      await load();
    } catch (e: any) { setError(e?.message || 'Failed to remove'); }
  };
  const decide = async (a: Assignment, approve: boolean, note?: string) => {
    setError(null); setOk(null);
    try {
      await rev(`/tier-assignments/${a.id}/${approve ? 'approve' : 'reject'}`, { method: 'POST', body: JSON.stringify({ note: note || null }) });
      setOk(approve ? `Approved. ${a.org_name || `cbo ${a.cbo_id}`} is now on ${a.tier_name} at the negotiated pricing.` : 'Rejected. The organization keeps its current plan.');
      setRejecting(null); setRejectNote('');
      await load();
    } catch (e: any) { setError(e?.message || 'Failed'); }
  };
  const updateUntil = async (a: Assignment, effective_until: string) => {
    setError(null); setOk(null);
    try {
      await rev(`/tier-assignments/${a.id}`, { method: 'PUT', body: JSON.stringify({ effective_until: effective_until || null }) });
      setOk('End date updated.');
      await load();
    } catch (e: any) { setError(e?.message || 'Failed to update'); }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Plans &amp; Organization Assignments</h3>
        </div>
        <span className="text-xs text-gray-400">Create/edit plans in the <strong>Plan Tiers</strong> section above</span>
      </div>

      {error && <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
      {ok && <div className="rounded bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">{ok}</div>}

      {/* Published plans */}
      <section>
        <h4 className="mb-2 text-sm font-semibold text-gray-700">Published plans (assignable to orgs)</h4>
        {loading ? (
          <div className="flex items-center text-sm text-gray-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading…</div>
        ) : tiers.length === 0 ? (
          <p className="text-sm text-gray-400">No published plans yet — create one above.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {tiers.map((t) => (
              <div key={t.id} className="rounded-md border border-gray-200 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{t.tier_name}</span>
                  <span className="text-xs text-gray-400">{modelLabel(t.pricing_model)}</span>
                </div>
                {(t as any).price_book_id && <span className="mt-1 inline-block rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] text-indigo-700">In price book</span>}
                <div className="mt-1 text-xs text-gray-500">
                  {hasBase(t.pricing_model) && t.base_monthly_price > 0 && <span>${t.base_monthly_price}/mo </span>}
                  {hasPerUnit(t.pricing_model) && t.per_user_price > 0 && <span>+ ${t.per_user_price}/{t.pricing_model === 'pmpm' ? 'member' : 'user'} </span>}
                  {hasRevShare(t.pricing_model) && t.revenue_share_config?.reimbursement_share_percent > 0 && <span>{t.revenue_share_config.reimbursement_share_percent}% reimb. </span>}
                  {t.setup_fee > 0 && <span>· setup ${t.setup_fee}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Assign with per-client overrides */}
      <AssignForm orgs={orgs} tiers={tiers} assignments={activeAssignments}
        onAssigned={async (msg: string) => { setOk(msg); await load(); }}
        onError={setError} />

      {/* Negotiated pricing waiting for a second operator */}
      {pending.length > 0 && (
        <section className="rounded-md border border-amber-200 bg-amber-50 p-4">
          <h4 className="mb-1 flex items-center gap-2 text-sm font-semibold text-amber-900"><ShieldCheck className="h-4 w-4" /> Pricing waiting for approval</h4>
          <p className="mb-3 text-xs text-amber-800">These are discounted beyond the price book's threshold. Each organization keeps its current plan until another operator approves.</p>
          <div className="space-y-2">
            {pending.map((a) => (
              <div key={a.id} className="rounded border border-amber-200 bg-white px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-medium">{a.org_name || `cbo ${a.cbo_id}`}</span> → {a.tier_name}
                    {a.override_per_user_price != null && <span className="text-gray-500"> · ${a.override_per_user_price}/user</span>}
                    {a.override_setup_fee != null && <span className="text-gray-500"> · setup ${a.override_setup_fee}</span>}
                    <div className="text-xs text-amber-800">{a.approval_reason}{a.approval_requested_by ? ` · requested by ${a.approval_requested_by}` : ''}</div>
                  </div>
                  {rejecting === a.id ? (
                    <div className="flex items-center gap-2">
                      <input value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Reason" className="rounded border border-gray-300 px-2 py-1 text-xs" />
                      <button onClick={() => decide(a, false, rejectNote)} className="text-xs font-medium text-red-600 hover:underline">Reject</button>
                      <button onClick={() => setRejecting(null)} className="text-xs text-gray-500 hover:underline">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <button onClick={() => openRevopsDocument(`/tier-assignments/${a.id}/proposal.html`).catch((e) => setError(e.message))} className="text-xs text-indigo-600 hover:underline">Proposal</button>
                      <button onClick={() => decide(a, true)} className="inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:underline"><Check className="h-3 w-3" /> Approve</button>
                      <button onClick={() => { setRejecting(a.id); setRejectNote(''); }} className="text-xs text-gray-600 hover:text-red-600">Reject…</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Current assignments */}
      <section>
        <h4 className="mb-2 text-sm font-semibold text-gray-700">Current assignments</h4>
        {activeAssignments.length === 0 ? (
          <p className="text-sm text-gray-400">No organizations are assigned a plan yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-gray-400">
                <th className="py-2 pr-4">Organization</th><th className="py-2 pr-4">Plan</th><th className="py-2 pr-4">Model</th><th className="py-2 pr-4">Since</th><th className="py-2 pr-4">Until</th><th className="py-2 pr-4"></th>
              </tr></thead>
              <tbody>
                {activeAssignments.map((a) => (
                  <tr key={a.id} className="border-t border-gray-100">
                    <td className="py-2 pr-4">{a.org_name || `cbo ${a.cbo_id}`}</td>
                    <td className="py-2 pr-4">{a.tier_name}</td>
                    <td className="py-2 pr-4">{modelLabel(a.pricing_model)}</td>
                    <td className="py-2 pr-4">{a.effective_from}</td>
                    <td className="py-2 pr-4">
                      <input
                        type="date"
                        defaultValue={a.effective_until || ''}
                        onBlur={(e) => { if ((e.target.value || '') !== (a.effective_until || '')) updateUntil(a, e.target.value); }}
                        className="border border-gray-200 rounded px-1.5 py-0.5 text-xs"
                        title="End date (blank = open-ended)"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <button onClick={() => openRevopsDocument(`/tier-assignments/${a.id}/proposal.html`).catch((e) => setError(e.message))} className="mr-3 text-xs text-indigo-600 hover:underline">Proposal</button>
                      <button onClick={() => removeAssignment(a)} className="text-xs text-red-600 hover:underline">Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ---- Assign to org with per-client overrides + cost preview -----------------

function AssignForm({ orgs, tiers, assignments, onAssigned, onError }: {
  orgs: Org[]; tiers: Tier[]; assignments: Assignment[];
  onAssigned: (msg: string) => void; onError: (m: string) => void;
}) {
  const [mode, setMode] = useState<'list' | 'custom'>('list');
  const [diff, setDiff] = useState<PricingDiff | null>(null);
  const [cboId, setCboId] = useState<number | undefined>(undefined);
  const [tierId, setTierId] = useState<string>('');
  const [ovrBase, setOvrBase] = useState('');
  const [ovrPerUnit, setOvrPerUnit] = useState('');
  const [ovrSetup, setOvrSetup] = useState('');
  const [ovrDisc, setOvrDisc] = useState('');
  const [ovrReimb, setOvrReimb] = useState('');
  // Per-org usage/token RATE overrides (written as org-specific consumption rows).
  const [rateSms, setRateSms] = useState('');
  const [rateEmail, setRateEmail] = useState('');
  const [rateAiIn, setRateAiIn] = useState('');
  const [rateAiOut, setRateAiOut] = useState('');
  const [count, setCount] = useState('10');
  const [effFrom, setEffFrom] = useState('');
  const [effUntil, setEffUntil] = useState('');
  const [preview, setPreview] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const tier = useMemo(() => tiers.find((t) => t.id === tierId), [tiers, tierId]);
  const model = tier?.pricing_model || 'per_seat';
  const assignedByCbo = useMemo(() => { const m: Record<number, Assignment> = {}; assignments.forEach((a) => (m[a.cbo_id] = a)); return m; }, [assignments]);

  // Live cost preview using the effective (override-or-template) numbers.
  useEffect(() => {
    if (!tier) { setPreview(null); return; }
    const b = num(ovrBase) ?? tier.base_monthly_price ?? 0;
    const pu = num(ovrPerUnit) ?? tier.per_user_price ?? 0;
    const n = parseInt(count, 10) || 0;
    let sub = 0;
    if (hasBase(model)) sub += b;
    if (hasPerUnit(model)) sub += pu * n;
    setPreview(Math.max(sub, 0));
  }, [tier, model, ovrBase, ovrPerUnit, count]);

  const buildBody = useCallback(() => {
    const body: any = { cbo_id: cboId, tier_template_id: tierId };
    if (mode === 'custom') {
      if (ovrBase !== '') body.override_base_monthly_price = num(ovrBase);
      if (ovrPerUnit !== '') body.override_per_user_price = num(ovrPerUnit);
      if (ovrSetup !== '') body.override_setup_fee = num(ovrSetup);
      if (ovrDisc !== '') body.override_annual_discount_percent = num(ovrDisc);
      if (hasRevShare(model) && ovrReimb !== '') body.override_revenue_share_config = { reimbursement_share_percent: num(ovrReimb) };
    }
    if (effFrom) body.effective_from = effFrom;
    if (effUntil) body.effective_until = effUntil;
    return body;
  }, [cboId, tierId, mode, ovrBase, ovrPerUnit, ovrSetup, ovrDisc, ovrReimb, model, effFrom, effUntil]);

  // Server-side list-vs-custom comparison (the same rule that decides approval).
  useEffect(() => {
    if (!tierId || mode !== 'custom') { setDiff(null); return; }
    const t = setTimeout(() => {
      rev<PricingDiff>('/tier-assignments/preview-pricing', { method: 'POST', body: JSON.stringify({ ...buildBody(), cbo_id: cboId || 0 }) })
        .then(setDiff).catch(() => setDiff(null));
    }, 300);
    return () => clearTimeout(t);
  }, [tierId, mode, buildBody, cboId]);

  const submit = async () => {
    if (!cboId) { onError('Select an organization'); return; }
    if (!tierId) { onError('Select a plan'); return; }
    setSaving(true);
    try {
      const res = await rev<any>('/tier-assignments', { method: 'POST', body: JSON.stringify(buildBody()) });
      if (res.status === 'pending_approval') {
        setOvrBase(''); setOvrPerUnit(''); setOvrSetup(''); setOvrDisc(''); setOvrReimb('');
        onAssigned(`Saved for approval: ${(res.reasons || []).join('; ')}. The organization keeps its current plan until another operator approves. Set custom usage rates after approval.`);
        return;
      }

      // Per-org usage/token RATE overrides → org-specific consumption rows
      // (target_organization_id). Metering prefers these over global rates.
      const rateWrites: Promise<any>[] = [];
      if (rateSms !== '') rateWrites.push(rev('/communication-pricing', { method: 'POST', body: JSON.stringify({ channel: 'sms', channel_display_name: 'SMS', unit_name: 'message', cost_per_unit: num(rateSms), target_organization_id: cboId }) }));
      if (rateEmail !== '') rateWrites.push(rev('/communication-pricing', { method: 'POST', body: JSON.stringify({ channel: 'email', channel_display_name: 'Email', unit_name: 'email', cost_per_unit: num(rateEmail), target_organization_id: cboId }) }));
      if (rateAiIn !== '' || rateAiOut !== '') rateWrites.push(rev('/ai-token-pricing', { method: 'POST', body: JSON.stringify({ service_name: 'AI Tokens', service_code: `ai_org_${cboId}`, input_rate_per_million: num(rateAiIn) || 0, output_rate_per_million: num(rateAiOut) || 0, target_organization_id: cboId }) }));
      if (rateWrites.length) await Promise.all(rateWrites);

      setOvrBase(''); setOvrPerUnit(''); setOvrSetup(''); setOvrDisc(''); setOvrReimb('');
      setRateSms(''); setRateEmail(''); setRateAiIn(''); setRateAiOut('');
      onAssigned(mode === 'list'
        ? 'Plan assigned at list price. Usage rates were set from the plan; see Consumption Pricing → Organization rates.'
        : 'Plan assigned with custom pricing. The org now sees it in Billing & Payments.');
    } catch (e: any) { onError(e?.message || 'Failed to assign'); }
    finally { setSaving(false); }
  };

  return (
    <section className="rounded-md border border-gray-200 p-4">
      <h4 className="mb-1 text-sm font-semibold text-gray-700">Assign a plan to an organization</h4>
      <p className="mb-3 text-xs text-gray-500">
        Overrides are <strong>per-client</strong> — they apply only to this org and never change the plan for anyone else. Leave blank to use the plan default.
      </p>
      <div className="mb-3 inline-flex rounded-md border border-gray-300 p-0.5 text-sm" role="radiogroup" aria-label="Pricing">
        <button role="radio" aria-checked={mode === 'list'} onClick={() => setMode('list')} className={`rounded px-3 py-1 ${mode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-600'}`}>List price (as published)</button>
        <button role="radio" aria-checked={mode === 'custom'} onClick={() => setMode('custom')} className={`rounded px-3 py-1 ${mode === 'custom' ? 'bg-indigo-600 text-white' : 'text-gray-600'}`}>Custom pricing</button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="block"><span className="text-xs font-medium text-gray-600">Organization</span>
          <select className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={cboId ?? ''} onChange={(e) => setCboId(e.target.value ? Number(e.target.value) : undefined)}>
            <option value="">Select…</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}{assignedByCbo[o.id] ? ` — on ${assignedByCbo[o.id].tier_name}` : ''}</option>)}
          </select></label>
        <label className="block"><span className="text-xs font-medium text-gray-600">Plan</span>
          <select className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={tierId} onChange={(e) => setTierId(e.target.value)}>
            <option value="">Select…</option>
            {tiers.map((t) => <option key={t.id} value={t.id}>{t.tier_name} ({modelLabel(t.pricing_model)})</option>)}
          </select></label>
        {tier && mode === 'custom' && hasBase(model) && (
          <label className="block"><span className="text-xs font-medium text-gray-600">Override base $/mo <span className="text-gray-400">(def ${tier.base_monthly_price})</span></span>
            <input type="number" min="0" step="0.01" className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={ovrBase} onChange={(e) => setOvrBase(e.target.value)} placeholder="default" /></label>
        )}
        {tier && mode === 'custom' && hasPerUnit(model) && (
          <label className="block"><span className="text-xs font-medium text-gray-600">Override {perUnitLabel(model)} $ <span className="text-gray-400">(def ${tier.per_user_price})</span></span>
            <input type="number" min="0" step="0.01" className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={ovrPerUnit} onChange={(e) => setOvrPerUnit(e.target.value)} placeholder="default" /></label>
        )}
        {tier && mode === 'custom' && hasRevShare(model) && (
          <label className="block"><span className="text-xs font-medium text-gray-600">Override reimbursement % <span className="text-gray-400">(def {tier.revenue_share_config?.reimbursement_share_percent ?? 0}%)</span></span>
            <input type="number" min="0" step="0.1" className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={ovrReimb} onChange={(e) => setOvrReimb(e.target.value)} placeholder="default" /></label>
        )}
        {tier && mode === 'custom' && (
          <label className="block"><span className="text-xs font-medium text-gray-600">Override setup $ <span className="text-gray-400">(def ${tier.setup_fee})</span></span>
            <input type="number" min="0" step="0.01" className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={ovrSetup} onChange={(e) => setOvrSetup(e.target.value)} placeholder="default" /></label>
        )}
      </div>

      {tier && hasPerUnit(model) && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm">
          <span className="text-gray-500">Preview at</span>
          <input type="number" min="0" className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm" value={count} onChange={(e) => setCount(e.target.value)} />
          <span className="text-gray-500">{unitNoun(model)} →</span>
          <span className="font-semibold text-gray-900">
            {preview === null ? '—' : `$${preview.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo`}
          </span>
        </div>
      )}

      {tier && mode === 'custom' && diff && diff.lines.length > 0 && (
        <div className={`mt-3 rounded-md border px-3 py-2 text-sm ${diff.needs_approval ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-gray-500"><th className="py-1">Term</th><th className="py-1">List</th><th className="py-1">This client</th><th className="py-1">Difference</th></tr></thead>
            <tbody>{diff.lines.map((l) => (
              <tr key={l.field}><td className="py-0.5">{l.label}</td><td>{l.list}</td><td>{l.custom}</td>
                <td className={l.discount_percent > 0 ? 'text-amber-700' : 'text-gray-500'}>{l.discount_percent > 0 ? `${l.discount_percent}% below list` : l.discount_percent < 0 ? `${-l.discount_percent}% above list` : '—'}</td></tr>
            ))}</tbody>
          </table>
          {diff.needs_approval && (
            <p className="mt-1 text-xs text-amber-800">Needs a second operator's approval ({diff.reasons.join('; ')}; threshold {diff.threshold_percent}%). The organization keeps its current plan until approved.</p>
          )}
        </div>
      )}

      {tier && mode === 'custom' && (
        <details className="mt-3 rounded-md border border-gray-200 p-3">
          <summary className="cursor-pointer text-xs font-medium text-gray-600">Usage &amp; token rate overrides (optional, per-client)</summary>
          <p className="mt-2 mb-2 text-xs text-gray-500">Set client-specific consumption rates. Blank = platform default. Metering applies the org rate when set.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block"><span className="text-xs text-gray-600">SMS $/message</span>
              <input type="number" min="0" step="0.0001" className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm" value={rateSms} onChange={(e) => setRateSms(e.target.value)} placeholder="default" /></label>
            <label className="block"><span className="text-xs text-gray-600">Email $/email</span>
              <input type="number" min="0" step="0.0001" className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm" value={rateEmail} onChange={(e) => setRateEmail(e.target.value)} placeholder="default" /></label>
            <label className="block"><span className="text-xs text-gray-600">AI input $/1M tokens</span>
              <input type="number" min="0" step="0.01" className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm" value={rateAiIn} onChange={(e) => setRateAiIn(e.target.value)} placeholder="default" /></label>
            <label className="block"><span className="text-xs text-gray-600">AI output $/1M tokens</span>
              <input type="number" min="0" step="0.01" className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm" value={rateAiOut} onChange={(e) => setRateAiOut(e.target.value)} placeholder="default" /></label>
          </div>
        </details>
      )}

      <div className="mt-3 flex flex-wrap gap-4">
        <label className="block"><span className="text-xs text-gray-600">Effective from</span>
          <input type="date" className="mt-1 block rounded-md border border-gray-300 px-2 py-1.5 text-sm" value={effFrom} onChange={(e) => setEffFrom(e.target.value)} /></label>
        <label className="block"><span className="text-xs text-gray-600">Effective until (optional)</span>
          <input type="date" className="mt-1 block rounded-md border border-gray-300 px-2 py-1.5 text-sm" value={effUntil} onChange={(e) => setEffUntil(e.target.value)} /></label>
      </div>

      <div className="mt-3">
        <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {mode === 'custom' && diff?.needs_approval ? 'Submit for approval' : 'Assign plan'}
        </button>
      </div>
    </section>
  );
}
